/**
 * The onboarding wizard (container, phase 7b item 3): a first show style
 * from a template (D32: the simulated devices' or vMix's), a studio from a
 * template naming it (the kind step 1's show style came from offered first;
 * and the pair's judgement), a server — a site and its install line — or the
 * cloud, and a first rundown (optionally seeded with the studio's template's
 * demo show), opened at the end (`@acp/engine/templates.js`).
 *
 * **Idempotent steps.** A step is done when the tenant already has what it
 * makes (`lib/onboarding.ts` reads the lists), and every request names a
 * client-chosen id — the show style `first-show-style`, the studio
 * `first-studio`, a site id and a rundown ULID minted once per page and saved
 * with the progress before the request — so a retry, a reload or a second tab
 * adopts what the first made instead of making another.
 *
 * **Progress** is saved per tenant in the cloud (`PUT /onboarding`). A node
 * saves none: the wizard works there from the lists alone, remembering a skip
 * for this page only, and never opens by itself.
 */
import type { OnboardingWire, OnboardingStepIdWire } from '@acp/api-contract/onboarding.js';
import { showStyleTemplateOf, studioTemplateOf, templateKindOfShowStyle, templateKindOfStudio } from '@acp/engine/templates.js';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  useAlertPickers,
  useCloudProductions,
  useCreateFirstRundown,
  useCreateShowStyle,
  useCreateSiteWithCode,
  useCreateStudio,
  useIdFactory,
  useMaybeStudio,
  useOnboarding,
  useRundowns,
  useServices,
  useShowStyle,
  useShowStyles,
  useStudios,
  useUpdateOnboarding,
  useValidateShowStyle,
} from '../app/hooks.js';
import type { SiteEnrolment } from '../api/onboarding.js';
import { RundownStep, ServerStep, ShowStyleStep, StudioStep } from '../components/onboarding/OnboardingSteps.js';
import { OnboardingWizard } from '../components/onboarding/OnboardingWizard.js';
import { rundownHref } from '../components/RundownList.js';
import { nextStep, wizardSteps, type WizardFacts, type WizardStep } from '../lib/onboarding.js';
import { simDefaultsFrom } from '../lib/studio/simDefaults.js';

/** The ids steps 1 and 2 make: fixed per tenant, so any retry finds the one already made. */
export const FIRST_SHOW_STYLE_ID = 'first-show-style';
export const FIRST_STUDIO_ID = 'first-studio';

/** Where 7b-B's site administration lives (a link from step 3). */
export const SITES_HREF = '/admin/sites';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function failed(err: unknown): void {
  toast.error(messageOf(err));
}

/** Record one step's outcome (a no-op answer where progress is not saved). */
type RecordStep = (id: OnboardingStepIdWire, status: 'pending' | 'done' | 'skipped', resourceId: string | null, choice: 'server' | 'cloud' | null) => Promise<unknown>;

type PanelProps = { readonly step: WizardStep | undefined; readonly record: RecordStep; readonly progress: OnboardingWire | null };

function ShowStylePanel({ step, record, onDone }: PanelProps & { readonly onDone: () => void }) {
  const showStyles = useShowStyles();
  const create = useCreateShowStyle();
  const id = step?.resourceId ?? null;
  return (
    <ShowStyleStep
      done={step?.status === 'done' ? { name: showStyles.data?.find((s) => s.showStyleId === id)?.name ?? id ?? '' } : null}
      pending={create.isPending}
      error={create.error === null ? null : messageOf(create.error)}
      onCreate={(name, kind) => {
        create
          .mutateAsync({ showStyleId: FIRST_SHOW_STYLE_ID, name, config: showStyleTemplateOf(kind) })
          .then((made) => record('showStyle', 'done', made.showStyleId, null))
          .then(onDone, () => undefined);
      }}
    />
  );
}

function StudioPanel({ step, record, showStyleId, pin }: PanelProps & { readonly showStyleId: string | null; readonly pin: () => void }) {
  const showStyles = useShowStyles();
  const studios = useStudios();
  const create = useCreateStudio();
  const [sim] = useState(() => simDefaultsFrom(typeof window === 'undefined' ? '' : window.location.search));
  const studioId = step?.status === 'done' ? step.resourceId : null;
  const studio = studios.data?.find((s) => s.studioId === studioId) ?? null;
  const validation = useValidateShowStyle(studio?.defaultShowStyleId ?? null, studioId);
  // Step 1's show style says which template's studio fits it (a vMix show style wants the vMix studio).
  const showStyle = useShowStyle(showStyleId).data;
  const defaultKind = (showStyle === undefined ? null : templateKindOfShowStyle(showStyle.config)) ?? 'sim';
  return (
    <StudioStep
      showStyles={(showStyles.data ?? []).map((s) => ({ id: s.showStyleId, name: s.name }))}
      defaultShowStyleId={showStyleId}
      defaultKind={defaultKind}
      defaultHost={sim.host}
      defaultBasePort={sim.basePort}
      done={studioId === null ? null : { name: studio?.name ?? studioId }}
      validation={validation.data === undefined ? null : { valid: validation.data.valid, problems: validation.data.problems.map((p) => `${p.path}: ${p.message}`) }}
      pending={create.isPending}
      error={create.error === null ? null : messageOf(create.error)}
      onCreate={(request) => {
        // Stay on this step once it is done: its panel then shows the pair's judgement.
        pin();
        create
          .mutateAsync({ studioId: FIRST_STUDIO_ID, name: request.name, siteId: null, defaultShowStyleId: request.showStyleId, config: studioTemplateOf(request.template) })
          .then((made) => record('studio', 'done', made.studioId, null))
          .catch(() => undefined);
      }}
    />
  );
}

function ServerPanel({ step, record, progress, onDone }: PanelProps & { readonly onDone: () => void }) {
  const navigate = useNavigate();
  const { backend } = useServices();
  const newId = useIdFactory();
  const create = useCreateSiteWithCode();
  const [picked, setPicked] = useState<'server' | 'cloud' | null>(null);
  const [enrolment, setEnrolment] = useState<SiteEnrolment | null>(null);
  const [siteId] = useState(() => `site-${newId().toLowerCase()}`);
  const choice = picked ?? progress?.steps.server?.choice ?? null;
  return (
    <ServerStep
      choice={choice}
      canAddServer={backend.kind === 'cloud'}
      enrolment={enrolment}
      done={step?.status === 'done'}
      pending={create.isPending}
      error={create.error === null ? null : messageOf(create.error)}
      onChoose={setPicked}
      onCreateSite={(name) => {
        record('server', 'pending', siteId, 'server')
          .then(() => create.mutateAsync({ siteId, name }))
          .then(setEnrolment)
          .catch(() => undefined);
      }}
      onCopy={(text) => {
        navigator.clipboard.writeText(text).then(() => toast.success('Copied'), failed);
      }}
      onOpenSites={() => {
        // 7b-B's site administration (not in this router's typed table yet): an ordinary navigation.
        window.location.assign(SITES_HREF);
      }}
      onOpenCloud={() => {
        record('server', 'done', 'cloud', 'cloud').then(() => navigate({ to: '/cloud-productions' }), failed);
      }}
      onDone={() => {
        const cloud = choice === 'cloud';
        record('server', 'done', cloud ? 'cloud' : (enrolment?.siteId ?? siteId), cloud ? 'cloud' : 'server').then(onDone, failed);
      }}
    />
  );
}

function RundownPanel({ step, record, progress, pin, studioId }: PanelProps & { readonly pin: () => void; readonly studioId: string | null }) {
  const navigate = useNavigate();
  const rundowns = useRundowns();
  // The demo show for the studio step 2 made: vMix's for a vMix studio, else the simulators'.
  const studioRead = useMaybeStudio(studioId);
  const studio = studioRead.data;
  const seedKind = (studio === undefined ? null : templateKindOfStudio(studio.config)) ?? 'sim';
  // Until the studio is read its kind is unknown: seeding the simulators' show into a vMix studio would be refused on air.
  const studioPending = studioId !== null && studioRead.isPending && studioRead.fetchStatus !== 'idle';
  const create = useCreateFirstRundown();
  const newId = useIdFactory();
  const [rundownId] = useState(newId);
  const doneId = step?.status === 'done' ? step.resourceId : null;
  return (
    <RundownStep
      done={doneId === null ? null : { id: doneId, name: rundowns.data?.find((r) => r.rundownId === doneId)?.name ?? 'your rundown' }}
      pending={create.isPending || studioPending}
      error={create.error === null ? null : messageOf(create.error)}
      onCreate={(name, seed) => {
        // Stay on this step once it is done: its panel then offers to open the rundown.
        pin();
        // The id is saved first, so a reload during the create adopts this rundown rather than making another.
        const id = progress?.steps.rundown?.resourceId ?? rundownId;
        record('rundown', 'pending', id, null)
          .then(() => create.mutateAsync({ rundownId: id, name, seed: seed ? seedKind : null }))
          .then((made) => record('rundown', 'done', made.rundownId, null))
          .catch(() => undefined);
      }}
      onOpen={(id) => {
        void navigate({ to: rundownHref(id) });
      }}
    />
  );
}

/** What the tenant already has (the lists the wizard reads its "done" from). */
function useWizardFacts(): WizardFacts {
  const showStyles = useShowStyles();
  const studios = useStudios();
  const rundowns = useRundowns();
  const sites = useAlertPickers().sites;
  const cloud = useCloudProductions();
  return {
    showStyleIds: (showStyles.data ?? []).map((s) => s.showStyleId),
    studioIds: (studios.data ?? []).map((s) => s.studioId),
    onPremSiteIds: sites.data === undefined ? null : sites.data.map((s) => s.id).filter((id) => id !== 'cloud'),
    cloudProductionCount: cloud.data === undefined ? null : cloud.data.cloudProductions.length,
    rundownIds: (rundowns.data ?? []).map((r) => r.rundownId),
  };
}

export function OnboardingView() {
  const navigate = useNavigate();
  const progressRead = useOnboarding(true);
  const update = useUpdateOnboarding();
  const facts = useWizardFacts();
  const [localSkips, setLocalSkips] = useState<ReadonlySet<OnboardingStepIdWire>>(new Set());
  const [open, setOpen] = useState<OnboardingStepIdWire | null | undefined>(undefined);
  const started = useRef(false);

  const persisted = progressRead.data?.persisted === true;
  const progress = progressRead.data?.persisted === true ? progressRead.data.onboarding : null;
  const steps = wizardSteps(facts, progress, localSkips);
  const shown = open === undefined ? nextStep(steps) : open;
  const stepOf = (id: OnboardingStepIdWire) => steps.find((s) => s.id === id);

  // Opening the wizard starts it (once per page): it will not open by itself again.
  const status = progress?.status ?? null;
  const { mutate: saveProgress } = update;
  useEffect(() => {
    if (status !== 'not-started' || started.current) return;
    started.current = true;
    saveProgress({ status: 'in-progress' });
  }, [status, saveProgress]);

  const record: RecordStep = (id, stepStatus, resourceId, choice) =>
    update.mutateAsync({ step: { id, status: stepStatus, ...(resourceId === null ? {} : { resourceId }), ...(choice === null ? {} : { choice }) } });
  const toNext = (): void => setOpen(undefined);
  const common = { record, progress };
  const panels: Readonly<Record<OnboardingStepIdWire, () => ReactNode>> = {
    showStyle: () => <ShowStylePanel {...common} step={stepOf('showStyle')} onDone={toNext} />,
    studio: () => <StudioPanel {...common} step={stepOf('studio')} showStyleId={stepOf('showStyle')?.resourceId ?? null} pin={() => setOpen('studio')} />,
    server: () => <ServerPanel {...common} step={stepOf('server')} onDone={toNext} />,
    rundown: () => <RundownPanel {...common} step={stepOf('rundown')} pin={() => setOpen('rundown')} studioId={stepOf('studio')?.resourceId ?? null} />,
  };
  const rundownId = stepOf('rundown')?.resourceId ?? null;

  return (
    <div className="grid gap-6" data-testid="onboarding-page">
      <OnboardingWizard
        steps={steps}
        open={shown}
        panel={shown === null ? null : panels[shown]()}
        persisted={persisted}
        onOpen={setOpen}
        onNext={toNext}
        onSkip={(id) => {
          setLocalSkips(new Set([...localSkips, id]));
          toNext();
          if (persisted) record(id, 'skipped', null, null).catch(failed);
        }}
        onClose={() => {
          if (persisted && progress?.status !== 'completed') update.mutate({ status: 'dismissed' });
          void navigate({ to: '/rundowns' });
        }}
        onFinish={() => {
          if (persisted) update.mutate({ status: 'completed' });
          void navigate({ to: rundownId === null ? '/rundowns' : rundownHref(rundownId) });
        }}
      />
    </div>
  );
}
